/**
 * Feedback System Type Definitions
 *
 * Types for the in-app feedback system that allows alpha/beta testers
 * to submit bugs, enhancements, and feature requests.
 */

export type FeedbackType = 'bug' | 'enhancement' | 'problem' | 'feature_request' | 'other';

export type FeedbackStatus = 'new' | 'in_progress' | 'resolved' | 'closed';

export type FeedbackPriority = 'low' | 'medium' | 'high' | 'critical';

export interface FeedbackSubmission {
  id: string;
  user_id: string;
  user_email: string;
  type: FeedbackType;
  title: string;
  description: string;
  page_url?: string;
  user_agent?: string;
  app_version?: string;
  priority: FeedbackPriority;
  status: FeedbackStatus;
  admin_notes?: string;
  created_at: string;
  updated_at: string;
  resolved_at?: string;
}

export interface FeedbackFormData {
  type: FeedbackType;
  title: string;
  description: string;
}

export interface FeedbackContext {
  page_url: string;
  user_agent: string;
  app_version: string;
}

export interface SubmitFeedbackInput {
  type: FeedbackType;
  title: string;
  description: string;
  page_url: string;
  user_agent: string;
  app_version: string;
}

export interface UpdateFeedbackInput {
  status?: FeedbackStatus;
  priority?: FeedbackPriority;
  admin_notes?: string;
  resolved_at?: string;
}

export interface FeedbackFilters {
  type?: FeedbackType | 'all';
  status?: FeedbackStatus | 'all';
  priority?: FeedbackPriority | 'all';
  search?: string;
  startDate?: Date;
  endDate?: Date;
}

export interface FeedbackStats {
  total: number;
  byType: Record<FeedbackType, number>;
  byStatus: Record<FeedbackStatus, number>;
  byPriority: Record<FeedbackPriority, number>;
}

// UI Helper Types
export const FEEDBACK_TYPE_LABELS: Record<FeedbackType, string> = {
  bug: 'Bug Report',
  enhancement: 'Enhancement',
  problem: 'Problem/Issue',
  feature_request: 'Feature Request',
  other: 'Other',
};

export const FEEDBACK_TYPE_COLORS: Record<FeedbackType, string> = {
  bug: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  enhancement: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  problem: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  feature_request: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  other: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
};

export const FEEDBACK_STATUS_LABELS: Record<FeedbackStatus, string> = {
  new: 'New',
  in_progress: 'In Progress',
  resolved: 'Resolved',
  closed: 'Closed',
};

export const FEEDBACK_STATUS_COLORS: Record<FeedbackStatus, string> = {
  new: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200',
  in_progress: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  resolved: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  closed: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
};

export const FEEDBACK_PRIORITY_LABELS: Record<FeedbackPriority, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  critical: 'Critical',
};

export const FEEDBACK_PRIORITY_COLORS: Record<FeedbackPriority, string> = {
  low: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
  medium: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  high: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  critical: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
};
